import React, { FC, useEffect, useMemo, useRef, useState } from "react"
import clsx from "clsx"

export type MultiSelectOption = {
  value: string
  label: string
}

type Props = {
  title?: string
  options: MultiSelectOption[]
  value: string[]
  onChange: (next: string[]) => void

  rounded?: boolean
  className?: string
  buttonClassName?: string
  dropdownClassName?: string

  allLabel?: string
}

export const MultiSelect: FC<Props> = ({
  title,
  options,
  value,
  onChange,

  rounded = true,
  className,
  buttonClassName,
  dropdownClassName,

  allLabel = "All"
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const [open, setOpen] = useState(false)

  const selectedSet = useMemo(() => new Set(value), [value])

  const buttonLabel = useMemo(() => {
    if (value.length === 0) return allLabel
    const labels = options.filter((o) => selectedSet.has(o.value)).map((o) => o.label)
    return labels.length ? labels.join(", ") : allLabel
  }, [allLabel, options, selectedSet, value.length])

  const toggleValue = (v: string) => {
    const next = new Set(value)
    if (next.has(v)) next.delete(v)
    else next.add(v)
    onChange(Array.from(next))
  }

  const setAll = () => onChange([])

  // close on outside click
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!open) return
      const el = rootRef.current
      if (!el) return
      if (!el.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onDoc)
    return () => document.removeEventListener("mousedown", onDoc)
  }, [open])

  // close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!open) return
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [open])

  return (
    <div ref={rootRef} className={clsx("relative", className)}>
      {title && (
        <label className="label block mb-1">
          <span className="label-text text-[rgba(6,7,21,1)] dark:text-white">{title}</span>
        </label>
      )}

      <button
        type="button"
        className={clsx(
          "select select-bordered outline-none !min-h-0 h-full py-2 text-sm pr-16 w-full text-left",
          rounded ? "rounded-full items-center pl-[24px] appearance-none" : "rounded-md pl-3",
          "bg-[#F5F5F5] text-black border-gray-200",
          "dark:bg-[#0C0C1A] dark:text-white dark:border-[rgba(255,255,255,0.1)]",
          buttonClassName
        )}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="block truncate">{buttonLabel}</span>
      </button>

      {open && (
        <div
          className={clsx(
            "absolute z-50 mt-2 w-full rounded-lg border p-3 shadow",
            "bg-white border-black/10",
            "dark:bg-[#0C0C1A] dark:border-[rgba(132,_0,_168,_0.6)]",
            dropdownClassName
          )}
        >
          <div className="flex gap-2 mb-3">
            <button type="button" className="btn btn-xs" onClick={setAll}>
              {allLabel}
            </button>
            <button type="button" className="btn btn-xs btn-primary ml-auto" onClick={() => setOpen(false)}>
              Done
            </button>
          </div>

          <div className="flex flex-col gap-2">
            {options.map((o) => (
              <label key={o.value} className="cursor-pointer label">
                <span className="label-text">{o.label}</span>
                <input type="checkbox" className="checkbox" checked={selectedSet.has(o.value)} onChange={() => toggleValue(o.value)} />
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
